import type { CSSProperties, ReactNode } from 'react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { StatusPill } from '../shared/pills'
import { useAdminOverview } from './admin-overview-provider'

const SEVERITY_META = {
  high: {
    label: 'High',
    tone: 'red',
    color: 'var(--cd-red)',
  },
  medium: {
    label: 'Medium',
    tone: 'amber',
    color: 'var(--cd-amber)',
  },
  low: {
    label: 'Low',
    tone: 'neutral',
    color: 'var(--cd-gray)',
  },
} as const

const DEFAULT_SEVERITY = SEVERITY_META.low

function severityMeta(
  severity: string,
): (typeof SEVERITY_META)[keyof typeof SEVERITY_META] {
  return (
    SEVERITY_META[severity as keyof typeof SEVERITY_META] ?? DEFAULT_SEVERITY
  )
}

function severityStyle(color: string): CSSProperties {
  return {
    background: `color-mix(in srgb, ${color} 8%, var(--cd-inset))`,
    borderColor: `color-mix(in srgb, ${color} 28%, var(--cd-border-2))`,
  }
}

export function RiskFlags(): ReactNode {
  const { insights, isLoading } = useAdminOverview()

  return (
    <Card className="h-full gap-4 border-(--cd-border) bg-(--cd-card) py-4 text-(--cd-text) shadow-none">
      <CardHeader className="gap-1 px-4">
        <CardTitle className="text-sm">⚠ Risk flags</CardTitle>
        <CardDescription className="text-xs text-(--cd-text-muted)">
          Auto-detected from homework, meetings, and submission activity
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-2.5 px-4">
        {isLoading ? (
          <p className="py-4 text-center text-xs text-(--cd-text-faint)">
            Loading risk flags…
          </p>
        ) : insights.length === 0 ? (
          <p className="py-4 text-center text-xs text-(--cd-text-faint)">
            No risk flags — nothing needs attention right now.
          </p>
        ) : (
          insights.map((flag) => {
            const meta = severityMeta(flag.severity)

            return (
              <Card
                key={flag.id}
                className="gap-2 border px-3 py-3 text-(--cd-text) shadow-none"
                style={severityStyle(meta.color)}
              >
                <div className="flex items-start justify-between gap-3">
                  <h4 className="text-xs font-semibold leading-5">
                    {flag.title}
                  </h4>
                  <StatusPill tone={meta.tone}>{meta.label}</StatusPill>
                </div>
                <p className="text-xs leading-5 text-(--cd-text-muted)">
                  {flag.body}
                </p>
              </Card>
            )
          })
        )}
      </CardContent>
    </Card>
  )
}
